// Inventory API
import { api, apiGet, apiPost, apiPatch, apiDelete } from './client'

export const inventoryApi = {
  // Warehouses
  listWarehouses: (params?: PaginationParams & { search?: string; is_active?: boolean }) =>
    apiGet<PaginatedResponse<Warehouse>>('/inventory/warehouses', params),

  getWarehouse: (id: string) =>
    apiGet<Warehouse>(`/inventory/warehouses/${id}`),

  createWarehouse: (data: WarehouseCreate) =>
    apiPost<Warehouse>('/inventory/warehouses', data),

  updateWarehouse: (id: string, data: WarehouseUpdate) =>
    apiPatch<Warehouse>(`/inventory/warehouses/${id}`, data),

  deleteWarehouse: (id: string) =>
    apiDelete(`/inventory/warehouses/${id}`),

  // Stock Levels
  getStockLevels: (params?: {
    product_id?: string
    variant_id?: string
    warehouse_id?: string
    low_stock_only?: boolean
  }) =>
    apiGet<StockLevel[]>(`/inventory/stock`, params),

  getProductStockSummary: (productId: string) =>
    apiGet<StockSummary>(`/inventory/stock/summary/${productId}`),

  // Stock Movements
  listMovements: (params?: MovementListParams) =>
    apiGet<PaginatedResponse<StockMovement>>('/inventory/movements', params),

  receiveStock: (data: StockReceiveRequest) =>
    apiPost<StockMovement>('/inventory/movements/receive', data),

  issueStock: (data: StockIssueRequest) =>
    apiPost<StockMovement>('/inventory/movements/issue', data),

  adjustStock: (data: StockAdjustRequest) =>
    apiPost<StockMovement>('/inventory/movements/adjust', data),

  // Transfers
  listTransfers: (params?: TransferListParams) =>
    apiGet<PaginatedResponse<StockTransfer>>('/inventory/transfers', params),

  getTransfer: (id: string) =>
    apiGet<StockTransfer>(`/inventory/transfers/${id}`),

  createTransfer: (data: StockTransferRequest) =>
    apiPost<StockTransfer>('/inventory/transfers', data),

  completeTransfer: (transferId: string) =>
    apiPost<StockTransfer>(`/inventory/transfers/${transferId}/complete`),

  cancelTransfer: (transferId: string) =>
    apiPost<StockTransfer>(`/inventory/transfers/${transferId}/cancel`),

  deleteTransfer: (id: string) =>
    apiDelete(`/inventory/transfers/${id}`),

  // Adjustments
  listAdjustments: (params?: AdjustmentListParams) =>
    apiGet<PaginatedResponse<StockAdjustment>>('/inventory/adjustments', params),

  getAdjustment: (id: string) =>
    apiGet<StockAdjustment>(`/inventory/adjustments/${id}`),

  createAdjustment: (data: StockAdjustmentRequest) =>
    apiPost<StockAdjustment>('/inventory/adjustments', data),

  completeAdjustment: (id: string) =>
    apiPost<StockAdjustment>(`/inventory/adjustments/${id}/complete`),

  cancelAdjustment: (id: string) =>
    apiPost<StockAdjustment>(`/inventory/adjustments/${id}/cancel`),

  deleteAdjustment: (id: string) =>
    apiDelete(`/inventory/adjustments/${id}`),

  // Receives (Purchase Orders / GRN)
  listReceives: (params?: ReceiveListParams) =>
    apiGet<PaginatedResponse<Receive>>('/inventory/receives', params),

  getReceive: (id: string) =>
    apiGet<Receive>(`/inventory/receives/${id}`),

  createReceive: (data: ReceiveCreate) =>
    apiPost<Receive>('/inventory/receives', data),

  updateReceive: (id: string, data: ReceiveUpdate) =>
    apiPatch<Receive>(`/inventory/receives/${id}`, data),

  deleteReceive: (id: string) =>
    apiDelete(`/inventory/receives/${id}`),

  completeReceive: (id: string) =>
    apiPost<Receive>(`/inventory/receives/${id}/complete`),

  cancelReceive: (id: string) =>
    apiPost<Receive>(`/inventory/receives/${id}/cancel`),

  // Low Stock
  getLowStock: (params?: LowStockParams) =>
    apiGet<PaginatedResponse<LowStockItem>>('/inventory/low-stock', params),

  exportLowStock: (params?: LowStockParams) =>
    apiGet<Blob>('/inventory/low-stock/export', params, { responseType: 'blob' }),

  // Cycle Counts
  listCycleCounts: (params?: CycleCountListParams) =>
    apiGet<PaginatedResponse<CycleCount>>('/inventory/cycle-counts', params),

  getCycleCount: (id: string) =>
    apiGet<CycleCount>(`/inventory/cycle-counts/${id}`),

  createCycleCount: (data: CycleCountCreate) =>
    apiPost<CycleCount>('/inventory/cycle-counts', data),

  startCycleCount: (id: string) =>
    apiPost<CycleCount>(`/inventory/cycle-counts/${id}/start`),

  completeCycleCount: (id: string) =>
    apiPost<CycleCount>(`/inventory/cycle-counts/${id}/complete`),

  cancelCycleCount: (id: string) =>
    apiPost<CycleCount>(`/inventory/cycle-counts/${id}/cancel`),

  deleteCycleCount: (id: string) =>
    apiDelete(`/inventory/cycle-counts/${id}`),
}

// Types
export interface PaginationParams {
  page?: number
  page_size?: number
  search?: string
  sort_by?: string
  sort_order?: 'asc' | 'desc'
}

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

export interface Warehouse {
  id: string
  name: string
  code: string
  description?: string
  address?: string
  city?: string
  phone?: string
  email?: string
  manager_id?: string
  is_default: boolean
  is_active: boolean
  settings: Record<string, any>
  created_at: string
  updated_at: string
}

export interface WarehouseCreate {
  name: string
  code: string
  description?: string
  address?: string
  city?: string
  phone?: string
  email?: string
  manager_id?: string
  is_default?: boolean
  is_active?: boolean
}

export interface WarehouseUpdate {
  name?: string
  address?: string
  city?: string
  phone?: string
  email?: string
  manager_id?: string
  is_default?: boolean
  is_active?: boolean
}

export interface StockLevel {
  id: string
  product_id: string
  variant_id?: string
  warehouse_id: string
  quantity: number
  reserved_quantity: number
  min_quantity: number
  max_quantity: number
  reorder_point?: number
  reorder_quantity?: number
  last_counted_at?: string
  product: { id: string; name: string; sku: string }
  variant?: { id: string; name: string; sku: string }
  warehouse: Warehouse
  available_quantity: number
  is_low_stock: boolean
  is_out_of_stock: boolean
  created_at: string
  updated_at: string
}

export interface StockSummary {
  product_id: string
  total_quantity: number
  total_available: number
  total_reserved: number
  by_warehouse: Array<{
    warehouse_id: string
    warehouse_name: string
    quantity: number
    reserved: number
    available: number
    min_qty: number
    max_qty: number
  }>
}

export interface MovementListParams extends PaginationParams {
  product_id?: string
  variant_id?: string
  warehouse_id?: string
  movement_type?: string
  date_from?: string
  date_to?: string
  reference_type?: string
  reference_id?: string
}

export interface StockMovement {
  id: string
  product_id: string
  variant_id?: string
  warehouse_id: string
  quantity: number
  movement_type: 'receive' | 'issue' | 'transfer' | 'adjust' | 'return' | 'waste' | 'count'
  reference_type?: string
  reference_id?: string
  batch_number?: string
  expiry_date?: string
  unit_cost?: number
  previous_quantity: number
  new_quantity: number
  notes?: string
  product: { id: string; name: string; sku: string }
  variant?: { id: string; name: string }
  warehouse: Warehouse
  user: { id: string; full_name: string }
  created_at: string
}

export interface StockReceiveRequest {
  product_id: string
  variant_id?: string
  warehouse_id: string
  quantity: number
  reference_type?: string
  reference_id?: string
  batch_number?: string
  expiry_date?: string
  unit_cost?: number
  notes?: string
}

export interface StockIssueRequest {
  product_id: string
  variant_id?: string
  warehouse_id: string
  quantity: number
  reference_type?: string
  reference_id?: string
  batch_number?: string
  notes?: string
}

export interface StockAdjustRequest {
  product_id: string
  variant_id?: string
  warehouse_id: string
  quantity: number
  notes?: string
}

export interface StockTransfer {
  id: string
  transfer_number: string
  from_warehouse_id: string
  to_warehouse_id: string
  status: 'pending' | 'in_transit' | 'completed' | 'cancelled'
  notes?: string
  completed_at?: string
  cancelled_at?: string
  from_warehouse: Warehouse
  to_warehouse: Warehouse
  lines: StockTransferLine[]
  created_at: string
  updated_at: string
}

export interface StockTransferLine {
  id: string
  product_id: string
  variant_id?: string
  quantity: number
  received_quantity: number
  product: { id: string; name: string; sku: string }
  variant?: { id: string; name: string }
}

export interface StockTransferRequest {
  from_warehouse_id: string
  to_warehouse_id: string
  items: Array<{
    product_id: string
    variant_id?: string
    quantity: number
  }>
  notes?: string
}

export interface TransferListParams extends PaginationParams {
  status?: 'pending' | 'in_transit' | 'completed' | 'cancelled'
  from_warehouse_id?: string
  to_warehouse_id?: string
  date_from?: string
  date_to?: string
}

export interface StockAdjustment {
  id: string
  adjustment_number: string
  warehouse_id: string
  status: 'draft' | 'pending' | 'completed' | 'cancelled'
  reason?: string
  notes?: string
  total_difference: number
  completed_at?: string
  lines: StockAdjustmentLine[]
  created_at: string
  updated_at: string
  warehouse: { id: string; name: string; code: string }
}

export interface AdjustmentListParams extends PaginationParams {
  status?: 'draft' | 'pending' | 'completed' | 'cancelled'
  warehouse_id?: string
  date_from?: string
  date_to?: string
}

export interface StockAdjustmentLine {
  id: string
  product_id: string
  variant_id?: string
  system_quantity: number
  counted_quantity: number
  difference: number
  reason?: string
  product: { id: string; name: string; sku: string }
  variant?: { id: string; name: string }
}

export interface StockAdjustmentRequest {
  warehouse_id: string
  items: Array<{
    product_id: string
    variant_id?: string
    counted_quantity: number
    reason?: string
  }>
  notes?: string
}

export interface LowStockParams extends PaginationParams {
  warehouse_id?: string
  severity?: 'critical' | 'warning' | 'normal'
}

export interface LowStockItem {
  product_id: string
  product_name: string
  product_sku: string
  variant_id?: string
  variant_name?: string
  warehouse_id: string
  warehouse_name: string
  current_quantity: number
  min_quantity: number
  max_quantity: number
  shortage: number
  severity: 'critical' | 'warning' | 'normal'
  suggested_reorder: number
}

// Receives (Purchase Orders / GRN)
export interface ReceiveListParams extends PaginationParams {
  supplier_id?: string
  warehouse_id?: string
  status?: 'pending' | 'processing' | 'completed' | 'cancelled'
  date_from?: string
  date_to?: string
}

export interface Receive {
  id: string
  receive_number: string
  supplier_id: string
  warehouse_id: string
  status: 'pending' | 'processing' | 'completed' | 'cancelled'
  expected_date?: string
  received_date?: string
  reference_number?: string
  notes?: string
  total_items: number
  total_quantity: number
  total_amount: number
  supplier: { id: string; name: string; code: string }
  warehouse: { id: string; name: string; code: string }
  items: ReceiveItem[]
  created_at: string
  updated_at: string
  completed_at?: string
  cancelled_at?: string
}

export interface ReceiveItem {
  id: string
  product_id: string
  variant_id?: string
  ordered_quantity: number
  received_quantity: number
  accepted_quantity: number
  rejected_quantity: number
  unit_cost: number
  batch_number?: string
  expiry_date?: string
  product: { id: string; name: string; sku: string; sale_price: number }
  variant?: { id: string; name: string; sku: string }
}

export interface ReceiveCreate {
  supplier_id: string
  warehouse_id: string
  expected_date?: string
  reference_number?: string
  notes?: string
  items: Array<{
    product_id: string
    variant_id?: string
    ordered_quantity: number
    unit_cost: number
    batch_number?: string
    expiry_date?: string
  }>
}

export interface ReceiveUpdate {
  expected_date?: string
  reference_number?: string
  notes?: string
  items?: Array<{
    id?: string
    product_id: string
    variant_id?: string
    ordered_quantity: number
    received_quantity: number
    accepted_quantity: number
    rejected_quantity: number
    unit_cost: number
    batch_number?: string
    expiry_date?: string
  }>
}

export interface CycleCount {
  id: string
  cycle_count_number: string
  warehouse_id: string
  status: 'draft' | 'in_progress' | 'completed' | 'cancelled'
  notes?: string
  started_at?: string
  completed_at?: string
  lines: CycleCountLine[]
  created_at: string
  updated_at: string
  warehouse: { id: string; name: string; code: string }
}

export interface CycleCountListParams extends PaginationParams {
  status?: 'draft' | 'in_progress' | 'completed' | 'cancelled'
  warehouse_id?: string
  date_from?: string
  date_to?: string
}

export interface CycleCountLine {
  id: string
  product_id: string
  variant_id?: string
  system_quantity: number
  counted_quantity?: number
  difference?: number
  counted_by?: string
  counted_at?: string
  product: { id: string; name: string; sku: string }
  variant?: { id: string; name: string }
  counter?: { id: string; full_name: string }
}

export interface CycleCountCreate {
  warehouse_id: string
  notes?: string
}